import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Shimmer } from '@wartek-id/shimmer'

import usePenetapanCountDinas from 'app/KinerjaDinasPage/hooks/usePenetapanCountDinas'
import SemiCircularProgress from 'app/KinerjaShared/components/SemiCircularProgress'
import { Header } from 'app/KinerjaShared/components/Header'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { PANDUAN_PENGELOLAAN_DINAS_URL } from 'app/KinerjaShared/utils/panduan'

interface PenetapanIndexProps {}

const LoadingBlock = () => {
  return (
    <div className="p-6 border border-subdued rounded-md flex flex-row items-center gap-9">
      <div
        className={clsx('w-56 h-36', {
          'flex items-center justify-center': true,
        })}
      >
        <Spinner size="lg" />
      </div>

      <div className="flex-1 flex flex-col items-start h-full">
        <Shimmer width={259} height={22} />
        <Shimmer width={413} height={22} className="mt-1" />
        <div className="flex flex-row items-center gap-4 mt-4">
          <Shimmer width={87} height={46} />
          <Shimmer width={345} height={22} />
        </div>
      </div>
    </div>
  )
}

const DinasPenetapanIndex: FC<PenetapanIndexProps> = ({}) => {
  const {
    isLoading: isLoadingPengawasProfile,
    profilePs: pengawasProfile,
    isKepalaDinas,
    leave,
  } = useRoleContext()
  if (!isLoadingPengawasProfile && !isKepalaDinas) leave()

  const { isLoading: isLoadingSchoolCount, data: counter } =
    usePenetapanCountDinas({ enabled: !!pengawasProfile })

  const countTotal = counter?.all || 0
  const countSent = counter?.sudahDikirim || 0
  let percentage = 0
  if (countSent === 0 && countSent === countTotal) {
    percentage = 0
  } else if (countSent >= countTotal) {
    percentage = 100
  } else if (countSent < countTotal) {
    percentage = (countSent / countTotal) * 100
  }

  return (
    <div>
      <Header
        title="Penetapan Predikat Kinerja"
        className="mb-9 print:hidden"
        rightNode={
          <div className="bg-white rounded">
            <Button
              as="a"
              variant="ghost"
              rel="noopener noreferrer"
              className="flex gap-1.5 h-14"
              size="md"
              href={PANDUAN_PENGELOLAAN_DINAS_URL}
              target="_blank"
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              Baca panduan
            </Button>
          </div>
        }
      />

      {/* Card: Langkah-langkah Penetapan ... */}
      <section className="bg-white mt-6 rounded-md px-6 py-4">
        {isLoadingSchoolCount ? (
          <div className="flex flex-row items-center justify-between">
            <Shimmer width={392} height={24} />
            <Shimmer width={378} height={38} className="mt-1" />
          </div>
        ) : (
          <div className="flex flex-row items-center justify-between">
            <Text variant="heading-md">
              Langkah-langkah Penetapan Predikat Kinerja
            </Text>
          </div>
        )}

        <div className="flex flex-col gap-4 mt-4 mx-2.5">
          {isLoadingSchoolCount ? (
            <LoadingBlock />
          ) : (
            <div className="p-6 border border-subdued rounded-md flex flex-row items-center gap-9">
              <div
                className={clsx('w-56 h-36', {
                  'flex items-center justify-center': isLoadingSchoolCount,
                })}
              >
                <SemiCircularProgress value={percentage}>
                  <div className={clsx('flex flex-col items-center', 'mb-12')}>
                    <Text
                      variant="heading-lg"
                      color="informational"
                      className="!text-blue-60"
                    >
                      {counter?.sudahDikirim || 0}/{counter?.all || 0}
                    </Text>
                    <Text variant="body" color="disabled">
                      Sudah dikirim
                    </Text>
                  </div>
                </SemiCircularProgress>
              </div>

              <div className="flex-1 flex flex-col items-start h-full">
                <Text variant="heading-sm">
                  Kirim Predikat Kinerja ke Pegawai
                </Text>
                <Text variant="body" color="subdued" className="mt-1">
                  Lakukan Penetapan Predikat Kinerja dan kirim ke Pegawai.
                </Text>

                {counter?.belumDikirim >= 1 ? (
                  <div className="flex flex-row items-center gap-4 mt-4">
                    <Link
                      href={`${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan/kirim`}
                      passHref
                    >
                      <Button as="a" color="blue" size="lg">
                        Mulai
                      </Button>
                    </Link>

                    <Text
                      variant="body"
                      color="informational"
                      className="!text-blue-60"
                    >
                      Ada Predikat Kinerja yang dapat dikirim sekarang
                    </Text>
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-4 mt-4">
                    <Link
                      href={`${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan/kirim`}
                      passHref
                    >
                      <Button as="a" color="white" size="lg">
                        Cek
                      </Button>
                    </Link>

                    <Text variant="body" color="disabled">
                      Belum ada Predikat Kinerja yang dapat dikirim sekarang
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center">
            <Icon className="!cursor-default">arrow_downward</Icon>
          </div>

          {isLoadingSchoolCount ? (
            <LoadingBlock />
          ) : (
            <div
              className={clsx(
                'p-6',
                'border border-subdued rounded-md',
                'flex flex-row items-center gap-9'
              )}
            >
              <Image
                alt=""
                height={84}
                width={84}
                src={'/illustrations/img-collect-files-folder.png'}
              />

              <div className="flex-1 flex flex-col items-start">
                <Text variant="heading-sm">Seluruh tahapan selesai</Text>
                <Text variant="body" color="subdued" className="mt-1">
                  Pegawai akan menerima Dokumen Evaluasi Kinerja. Tim Kerja akan
                  menerima data Predikat Kinerja yang dikirim ke Pegawai.
                </Text>

                <Button
                  as="a"
                  variant="ghost"
                  rel="noopener noreferrer"
                  className="flex gap-1.5 mt-1"
                  size="lg"
                  href={PANDUAN_PENGELOLAAN_DINAS_URL}
                  target="_blank"
                >
                  <Icon fontSize="sm" color="informational">
                    help
                  </Icon>
                  Bagaimana tampilan di Pegawai dan Tim Kerja?
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default DinasPenetapanIndex
